<template>
  <!-- With presence: positioned wrapper + dot overlay -->
  <div v-if="showPresence" class="relative inline-flex items-center justify-center shrink-0" :style="wrapperStyle">
    <div
      v-if="customAvatar"
      class="profile_icon profile_default_color"
      :style="circleStyle"
    >
      <ThemeImage
        :class="`w-[${customAvatar?.size}px] h-[${customAvatar?.size}px]`"
        :icon="customAvatar?.icon"
        :alt="`${customAvatar?.id}_icon`"
      />
    </div>

    <div v-else-if="showValidImageAvatar" class="profile_icon w-fit h-fit">
      <img
        class="rounded-full"
        :style="circleStyle"
        :src="avatarUrl"
        alt="avatar"
        @load="handleImageLoad"
        @error="handleImageError"
        v-show="imageLoaded"
        ref="imageRef"
      />
      <SkeletonLoader
        :page="loaderPages.PROFILE_ICON"
        :rsk-size="size"
        v-show="!imageLoaded && !imageError"
      />
    </div>

    <div
      v-else-if="initialsContent !== '' && initialsContent !== 'N/A'"
      :class="['profile_icon items-center justify-center', initialsAvatarClass]"
      :style="initialsAvatarStyle"
    >
      {{ initialsContent }}
    </div>

    <div
      v-else
      class="profile_icon items-center justify-center"
      :style="{ ...circleStyle, ...nullEntityColorStyle }"
    >
      <ThemeImage
        :class="`w-[${placeholderSize}px] h-[${placeholderSize}px]`"
        :icon="imageNames.NULL_ENTITY_PLACEHOLDER"
        alt="null_entity_placeholder"
      />
    </div>

    <span
      class="absolute bottom-0 right-0 rounded-full border-solid box-border"
      :class="isOnline ? 'bg-green-500' : 'bg-gray-500'"
      :style="{ ...presenceDotStyle, borderColor: 'var(--background-color, #1e1e1e)' }"
    />
  </div>

  <!-- Without presence: no wrapper, renders bare avatar div (preserves external class/positioning) -->
  <div
    v-else-if="customAvatar"
    class="profile_icon profile_default_color"
    :style="circleStyle"
  >
    <ThemeImage
      :class="`w-[${customAvatar?.size}px] h-[${customAvatar?.size}px]`"
      :icon="customAvatar?.icon"
      :alt="`${customAvatar?.id}_icon`"
    />
  </div>

  <div v-else-if="showValidImageAvatar" class="profile_icon w-fit h-fit">
    <img
      class="rounded-full"
      :style="circleStyle"
      :src="avatarUrl"
      alt="avatar"
      @load="handleImageLoad"
      @error="handleImageError"
      v-show="imageLoaded"
      ref="imageRef"
    />
    <SkeletonLoader
      :page="loaderPages.PROFILE_ICON"
      :rsk-size="size"
      v-show="!imageLoaded && !imageError"
    />
  </div>

  <div
    v-else-if="initialsContent !== '' && initialsContent !== 'N/A'"
    :class="['profile_icon items-center justify-center', initialsAvatarClass]"
    :style="initialsAvatarStyle"
  >
    {{ initialsContent }}
  </div>

  <div
    v-else
    class="profile_icon items-center justify-center"
    :style="{ ...circleStyle, ...nullEntityColorStyle }"
  >
    <ThemeImage
      :class="`w-[${placeholderSize}px] h-[${placeholderSize}px]`"
      :icon="imageNames.NULL_ENTITY_PLACEHOLDER"
      alt="null_entity_placeholder"
    />
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { placeholderImagesToSkip, colorSchema } from "@/src/constants/colorSchema.js";
import {
  getInitials,
  getAuthUserProfileColor,
  getProfileColor,
  renderEntity,
} from "@/src/utils/utils.js";

import ThemeImage from "@/src/components/ThemeImage.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";

const store = useStore();

const PALETTE_COLORS = Object.values(colorSchema);

const props = defineProps({
  data: { type: [Object, String], required: true },
  size: { type: [Number, String], default: 28 },
  font: { type: [Number, String], default: 12 },
  avatar: { type: String, default: null },
  view: { type: Boolean, default: false },
  param: { type: String, default: null },
  initialCount: { type: Number, default: 2 },
  customAvatar: { type: Object, default: null },
  borderRadius: { type: [Number, String], default: 50 },
  placeholderSize: { type: [Number, String], default: 24 },
  showPresence: { type: Boolean, default: false },
  userId: { type: [Number, String], default: null },
});

const isOnline = computed(() => {
  if (!props.showPresence || !props.userId) return false;
  return store.getters["users/isUserOnline"](props.userId);
});

const sizeNum = computed(() => typeof props.size === "number" ? props.size : parseInt(props.size, 10) || 28);

const wrapperStyle = computed(() => {
  const s = `${sizeNum.value}px`;
  return { position: "relative", width: s, height: s, flexShrink: 0 };
});

const presenceDotStyle = computed(() => {
  const dotSize = Math.max(8, Math.round(sizeNum.value * 0.3));
  return {
    width: `${dotSize}px`,
    height: `${dotSize}px`,
    borderWidth: `${Math.max(1.5, Math.round(dotSize * 0.2))}px`,
  };
});

const imageLoaded = ref(false);
const imageError = ref(false);
const imageRef = ref(null);

const showValidImageAvatar = computed(() => {
  if (imageError.value) return false;
  const url = avatarUrl.value;
  return !!(url && !isPlaceholderImage(url));
});

const avatarUrl = computed(() => {
  if (props.avatar) {
    return props.data[props.avatar];
  }
  if (!props.param && !props.view && !props.avatar) {
    // Priority: uploaded profile_pic > logo_url > avatar_url
    // Skip avatar_url if it's a generated placeholder (ui-avatars) — prefer avatar_color initials instead
    const pic = props.data?.profile_pic || props.data?.logo_url;
    if (pic) return pic;
    const avatarUrl = props.data?.avatar_url;
    if (avatarUrl && !avatarUrl.includes("ui-avatars.com")) return avatarUrl;
    return "";
  }
  return "";
});

const initialsContent = computed(() => {
  let text;
  if (props.view && props.param) {
    text = getInitials(props.data[props.param]);
  } else {
    const entityText =
      renderEntity(props.data) ||
      props.data?.display_name ||
      props.data?.phone_number ||
      "";
    text = getInitials(entityText);
  }

  if (props.initialCount && text) {
    return text.slice(0, props.initialCount);
  }

  return text;
});

const circleStyle = computed(() => {
  const sizeValue =
    typeof props.size === "number" ? `${props.size}px` : props.size;
  const fontValue =
    typeof props.font === "number" ? `${props.font}px` : props.font;
  const radiusValue =
    typeof props.borderRadius === "number"
      ? `${props.borderRadius}px`
      : props.borderRadius;
  return {
    minWidth: sizeValue,
    minHeight: sizeValue,
    width: sizeValue,
    height: sizeValue,
    fontSize: fontValue,
    borderRadius: radiusValue,
  };
});

const authUserColorResult = computed(() => {
  if (typeof props.data === "object" && props.data !== null) {
    return getAuthUserProfileColor(props.data);
  }
  return { style: null, class: getProfileColor(initialsContent.value) };
});

// Always returns a visible palette color for null/placeholder entities.
// Priority: inline style from authUserColorResult (id/name hash) → fallback hash from data object.
const nullEntityColorStyle = computed(() => {
  if (authUserColorResult.value.style) return authUserColorResult.value.style;
  // authUserColorResult fell back to a CSS class — generate a stable color from available data
  let seed = 0;
  if (typeof props.data === "object" && props.data !== null) {
    const str = JSON.stringify(props.data);
    for (let i = 0; i < str.length; i++) {
      seed = (seed * 31 + str.charCodeAt(i)) >>> 0;
    }
  }
  return { backgroundColor: PALETTE_COLORS[seed % PALETTE_COLORS.length] };
});

const initialsAvatarClass = computed(() => authUserColorResult.value.class);
const initialsAvatarStyle = computed(() => ({
  ...circleStyle.value,
  ...(authUserColorResult.value.style || {}),
}));

const isPlaceholderImage = (url) => {
  try {
    return placeholderImagesToSkip.some((placeholder) =>
      url.includes(placeholder),
    );
  } catch (error) {
    console.error(
      "An error occurred while checking if placeholder image or not: ",
      error,
    );
  }
};

const handleImageLoad = () => {
  try {
    imageLoaded.value = true;
    imageError.value = false;
  } catch (error) {
    console.error("An error occurred while handling image load: ", error);
  }
};

const handleImageError = () => {
  try {
    imageLoaded.value = false;
    imageError.value = true;
  } catch (error) {
    console.error("An error occurred while handling image error: ", error);
  }
};

onMounted(() => {
  if (imageRef.value && imageRef.value.complete) {
    if (imageRef.value.naturalWidth === 0) {
      handleImageError();
    } else {
      handleImageLoad();
    }
  }
});
</script>

<style scoped>
.profile_icon {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  color: white;
  text-align: center;
  font-family: "Proxima Nova", sans-serif;
  font-style: normal;
  font-weight: 500;
}
</style>
